import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Cog6ToothIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  DocumentTextIcon,
  GlobeAltIcon,
  CurrencyDollarIcon,
  ShieldCheckIcon,
  ClockIcon,
  PhotoIcon,
  ArchiveBoxIcon,
  BellIcon
} from '@heroicons/react/24/outline';

import { Card, CardHeader, CardTitle, CardContent } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Input } from '../../components/common/Input';
import { Select } from '../../components/common/Select';
import { Checkbox } from '../../components/common/Checkbox';
import { Textarea } from '../../components/common/Textarea';
import { useNotification } from '../../contexts/NotificationContext';
import { usePermissions } from '../../hooks/usePermissions';
import { pimService } from '../../services/pimService';
import { useTranslation } from 'react-i18next';

interface PimSettings {
  general: {
    systemName: string;
    defaultLanguage: string;
    dateFormat: string;
    currency: string;
    currencySymbol: string;
    enableMultiLanguage: boolean;
    enableCategories: boolean;
    enableVariants: boolean;
    enableBundles: boolean;
  };
  products: {
    defaultStatus: string;
    autoGenerateSlug: boolean;
    autoGenerateSku: boolean;
    skuPrefix: string;
    requireImages: boolean;
    maxImagesPerProduct: number;
    enableStockManagement: boolean;
    enablePricing: boolean;
    enableSeo: boolean;
    enableTags: boolean;
  };
  workflow: {
    enableWorkflow: boolean;
    defaultWorkflowState: string;
    autoAssignReviewer: boolean;
    requireApprovalComments: boolean;
    enableEmailNotifications: boolean;
    notificationRecipients: string[];
    workflowTimeout: number;
    enableRevisionControl: boolean;
    maxRevisions: number;
  };
  media: {
    maxFileSize: number;
    allowedFileTypes: string[];
    autoOptimizeImages: boolean;
    generateThumbnails: boolean;
    thumbnailSizes: string[];
    enableWatermark: boolean;
    watermarkText: string;
    defaultAltText: string;
    enableCdn: boolean;
    cdnUrl: string;
  };
  api: {
    enablePublicApi: boolean;
    enableAuthentication: boolean;
    defaultRateLimit: number;
    enableCors: boolean;
    corsOrigins: string[];
    enableLogging: boolean;
    logLevel: string;
    enableCaching: boolean;
    cacheTimeout: number;
  };
  backup: {
    enableAutoBackup: boolean;
    backupFrequency: string;
    backupRetention: number;
    includeMedia: boolean;
    backupLocation: string;
    enableEncryption: boolean;
    notifyOnBackup: boolean;
  };
}

const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'pl', label: 'Polski' },
  { value: 'de', label: 'Deutsch' },
  { value: 'fr', label: 'Français' },
  { value: 'es', label: 'Español' },
];

const DATE_FORMAT_OPTIONS = [
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (2025-10-23)' },
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (23/10/2025)' },
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (10/23/2025)' },
  { value: 'DD.MM.YYYY', label: 'DD.MM.YYYY (23.10.2025)' },
];

const CURRENCY_OPTIONS = [
  { value: 'EUR', label: 'Euro (EUR)' },
  { value: 'USD', label: 'US Dollar (USD)' },
  { value: 'PLN', label: 'Polish Złoty (PLN)' },
  { value: 'GBP', label: 'British Pound (GBP)' },
];

const FILE_TYPE_OPTIONS = [
  { value: 'jpg', label: 'JPEG' },
  { value: 'png', label: 'PNG' },
  { value: 'gif', label: 'GIF' },
  { value: 'webp', label: 'WebP' },
  { value: 'pdf', label: 'PDF' },
  { value: 'svg', label: 'SVG' },
];

export const Settings = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('general');
  const { showNotification } = useNotification();
  const permissions = usePermissions();
  const queryClient = useQueryClient();
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const { register, handleSubmit, reset, watch, formState: { errors, isDirty } } = useForm<PimSettings>();

  // Fetch current settings
  const { data: settings, isLoading } = useQuery({
    queryKey: ['pim-settings'],
    queryFn: () => pimService.getSettings()
  });

  // Reset form when settings data is loaded
  useEffect(() => {
    if (settings) {
      // Transform array fields to comma-separated strings for form display
      const formData = {
        ...settings,
        workflow: {
          ...settings.workflow,
          notificationRecipients: Array.isArray(settings.workflow?.notificationRecipients) 
            ? settings.workflow.notificationRecipients.join(', ')
            : settings.workflow?.notificationRecipients || ''
        },
        media: {
          ...settings.media,
          allowedFileTypes: Array.isArray(settings.media?.allowedFileTypes)
            ? settings.media.allowedFileTypes
            : [],
          thumbnailSizes: Array.isArray(settings.media?.thumbnailSizes)
            ? settings.media.thumbnailSizes.join(', ')
            : settings.media?.thumbnailSizes || ''
        },
        api: {
          ...settings.api,
          corsOrigins: Array.isArray(settings.api?.corsOrigins)
            ? settings.api.corsOrigins.join(', ')
            : settings.api?.corsOrigins || ''
        }
      };
      reset(formData);
    }
  }, [settings, reset]);

  // Update settings mutation
  const updateMutation = useMutation({
    mutationFn: (data: Partial<PimSettings>) => pimService.updateSettings(data),
    onSuccess: () => {
      showNotification(t('pim.settings.messages.updated'), 'success');
      queryClient.invalidateQueries(['pim-settings']);
      setHasUnsavedChanges(false);
    },
    onError: (error: any) => {
      showNotification(error.message || t('pim.settings.messages.error'), 'error');
    }
  });

  // Test connection mutations
  const testDatabaseMutation = useMutation({
    mutationFn: () => pimService.testDatabaseConnection(),
    onSuccess: () => showNotification(t('pim.settings.messages.dbSuccess'), 'success'),
    onError: () => showNotification(t('pim.settings.messages.dbError'), 'error')
  });

  const testEmailMutation = useMutation({
    mutationFn: () => pimService.testEmailConfiguration(),
    onSuccess: () => showNotification(t('pim.settings.messages.emailSuccess'), 'success'),
    onError: () => showNotification(t('pim.settings.messages.emailError'), 'error')
  });

  // Watch for changes
  const watchedValues = watch();
  useEffect(() => {
    setHasUnsavedChanges(isDirty);
  }, [isDirty]);

  const onSubmit = (data: PimSettings) => {
    // Transform comma-separated strings back to arrays
    const processedData = {
      ...data,
      workflow: {
        ...data.workflow,
        notificationRecipients: typeof data.workflow?.notificationRecipients === 'string'
          ? data.workflow.notificationRecipients.split(',').map(email => email.trim()).filter(email => email.length > 0)
          : data.workflow?.notificationRecipients || []
      },
      media: {
        ...data.media,
        thumbnailSizes: typeof data.media?.thumbnailSizes === 'string'
          ? data.media.thumbnailSizes.split(',').map(size => size.trim()).filter(size => size.length > 0)
          : data.media?.thumbnailSizes || []
      },
      api: {
        ...data.api,
        corsOrigins: typeof data.api?.corsOrigins === 'string'
          ? data.api.corsOrigins.split(',').map(origin => origin.trim()).filter(origin => origin.length > 0)
          : data.api?.corsOrigins || []
      }
    };
    
    updateMutation.mutate(processedData);
  };

  const handleTabChange = (tab: string) => {
    if (hasUnsavedChanges) {
      if (confirm(t('pim.settings.unsavedPrompt'))) {
        setActiveTab(tab);
        setHasUnsavedChanges(false);
        reset();
      }
    } else {
      setActiveTab(tab);
    }
  };

  const tabs = [
    { id: 'general', label: t('pim.settings.tabs.general'), icon: Cog6ToothIcon },
    { id: 'products', label: t('pim.settings.tabs.products'), icon: ArchiveBoxIcon },
    { id: 'workflow', label: t('pim.settings.tabs.workflow'), icon: CheckCircleIcon },
    { id: 'media', label: t('pim.settings.tabs.media'), icon: PhotoIcon },
    { id: 'api', label: t('pim.settings.tabs.api'), icon: ShieldCheckIcon },
    { id: 'backup', label: t('pim.settings.tabs.backup'), icon: DocumentTextIcon },
  ];

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        <span className="ml-2">{t('pim.settings.loading')}</span>
      </div>
    );
  }

  if (!permissions.canManagePim) {
    return (
      <div className="text-center py-12">
        <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">{t('pim.settings.messages.accessDenied')}</h3>
        <p className="mt-1 text-sm text-gray-500">{t('pim.settings.messages.accessDeniedDesc')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('pim.settings.title')}</h1>
          <p className="text-gray-600">{t('pim.settings.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => testDatabaseMutation.mutate()}
            disabled={testDatabaseMutation.isLoading}
          >
            {t('pim.settings.testDatabase')}
          </Button>
          <Button
            variant="outline"
            onClick={() => testEmailMutation.mutate()}
            disabled={testEmailMutation.isLoading}
          >
            {t('pim.settings.testEmail')}
          </Button>
        </div>
      </div>

      {/* Unsaved changes warning */}
      {hasUnsavedChanges && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mr-2" />
            <p className="text-sm text-yellow-800">
              {t('pim.settings.unsavedChanges')}
            </p>
          </div>
        </div>
      )}

      <div className="flex">
        {/* Sidebar Navigation */}
        <div className="w-64 mr-6">
          <nav className="space-y-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`
                  w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors
                  ${activeTab === tab.id
                    ? 'bg-primary-100 text-primary-700 border-r-2 border-primary-500'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }
                `}
              >
                <tab.icon className="mr-3 h-5 w-5" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Main Content */}
        <div className="flex-1">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            
            {/* General Settings */}
            {activeTab === 'general' && (
              <Card>
                <CardHeader>
                  <CardTitle>{t('pim.settings.general.title')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label={t('pim.settings.general.systemName')}
                      {...register('general.systemName', { required: t('pim.settings.general.systemNameRequired') })}
                      error={errors.general?.systemName?.message}
                    />
                    <Select
                      label={t('pim.settings.general.defaultLanguage')}
                      {...register('general.defaultLanguage')}
                      options={LANGUAGE_OPTIONS}
                    />
                    <Select
                      label={t('pim.settings.general.dateFormat')}
                      {...register('general.dateFormat')}
                      options={DATE_FORMAT_OPTIONS}
                    />
                    <Select
                      label={t('pim.settings.general.currency')}
                      {...register('general.currency')}
                      options={CURRENCY_OPTIONS}
                    />
                    <Input
                      label={t('pim.settings.general.currencySymbol')}
                      placeholder={t('pim.settings.general.currencySymbolPlaceholder')}
                      {...register('general.currencySymbol')}
                    />
                  </div>

                  <div className="space-y-3">
                    <h4 className="text-sm font-medium text-gray-900">{t('pim.settings.general.features')}</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <Checkbox
                        label={t('pim.settings.general.enableMultiLanguage')}
                        {...register('general.enableMultiLanguage')}
                      />
                      <Checkbox
                        label={t('pim.settings.general.enableCategories')}
                        {...register('general.enableCategories')}
                      />
                      <Checkbox
                        label={t('pim.settings.general.enableVariants')}
                        {...register('general.enableVariants')}
                      />
                      <Checkbox
                        label={t('pim.settings.general.enableBundles')}
                        {...register('general.enableBundles')}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Product Settings */}
            {activeTab === 'products' && (
              <Card>
                <CardHeader>
                  <CardTitle>{t('pim.settings.products.title')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Select
                      label={t('pim.settings.products.defaultStatus')}
                      {...register('products.defaultStatus')}
                      options={[
                        { value: 'draft', label: t('pim.settings.products.statuses.draft') },
                        { value: 'pending_review', label: t('pim.settings.products.statuses.pending_review') },
                        { value: 'published', label: t('pim.settings.products.statuses.published') }
                      ]}
                    />
                    <Input
                      label={t('pim.settings.products.skuPrefix')}
                      placeholder={t('pim.settings.products.skuPrefixPlaceholder')}
                      {...register('products.skuPrefix')}
                    />
                    <Input
                      label={t('pim.settings.products.maxImages')}
                      type="number"
                      {...register('products.maxImagesPerProduct', { valueAsNumber: true })}
                    />
                  </div>

                  <div className="space-y-3">
                    <h4 className="text-sm font-medium text-gray-900">{t('pim.settings.products.features')}</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <Checkbox
                        label={t('pim.settings.products.autoGenerateSlug')}
                        {...register('products.autoGenerateSlug')}
                      />
                      <Checkbox
                        label={t('pim.settings.products.autoGenerateSku')}
                        {...register('products.autoGenerateSku')}
                      />
                      <Checkbox
                        label={t('pim.settings.products.requireImages')}
                        {...register('products.requireImages')}
                      />
                      <Checkbox
                        label={t('pim.settings.products.enableStockManagement')}
                        {...register('products.enableStockManagement')}
                      />
                      <Checkbox
                        label={t('pim.settings.products.enablePricing')}
                        {...register('products.enablePricing')}
                      />
                      <Checkbox
                        label={t('pim.settings.products.enableSeo')}
                        {...register('products.enableSeo')}
                      />
                      <Checkbox
                        label={t('pim.settings.products.enableTags')}
                        {...register('products.enableTags')}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Workflow Settings */}
            {activeTab === 'workflow' && (
              <Card>
                <CardHeader>
                  <CardTitle>{t('pim.settings.workflow.title')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Select
                      label={t('pim.settings.workflow.defaultState')}
                      {...register('workflow.defaultWorkflowState')}
                      options={[
                        { value: 'draft', label: t('pim.settings.workflow.states.draft') },
                        { value: 'pending_review', label: t('pim.settings.workflow.states.pending_review') },
                        { value: 'in_review', label: t('pim.settings.workflow.states.in_review') }
                      ]}
                    />
                    <Input
                      label={t('pim.settings.workflow.workflowTimeout')}
                      type="number"
                      {...register('workflow.workflowTimeout', { valueAsNumber: true })}
                    />
                    <Input
                      label={t('pim.settings.workflow.maxRevisions')}
                      type="number"
                      {...register('workflow.maxRevisions', { valueAsNumber: true })}
                    />
                  </div>

                  <div className="space-y-3">
                    <h4 className="text-sm font-medium text-gray-900">{t('pim.settings.workflow.features')}</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <Checkbox
                        label={t('pim.settings.workflow.enableWorkflow')}
                        {...register('workflow.enableWorkflow')}
                      />
                      <Checkbox
                        label={t('pim.settings.workflow.autoAssignReviewer')}
                        {...register('workflow.autoAssignReviewer')}
                      />
                      <Checkbox
                        label={t('pim.settings.workflow.requireComments')}
                        {...register('workflow.requireApprovalComments')}
                      />
                      <Checkbox
                        label={t('pim.settings.workflow.enableNotifications')}
                        {...register('workflow.enableEmailNotifications')}
                      />
                      <Checkbox
                        label={t('pim.settings.workflow.enableRevisionControl')}
                        {...register('workflow.enableRevisionControl')}
                      />
                    </div>
                  </div>

                  <Textarea
                    label={t('pim.settings.workflow.notificationRecipients')}
                    placeholder={t('pim.settings.workflow.notificationRecipientsPlaceholder')}
                    {...register('workflow.notificationRecipients')}
                  />
                </CardContent>
              </Card>
            )}

            {/* Media Settings */}
            {activeTab === 'media' && (
              <Card>
                <CardHeader>
                  <CardTitle>{t('pim.settings.media.title')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label={t('pim.settings.media.maxFileSize')}
                      type="number"
                      {...register('media.maxFileSize', { valueAsNumber: true })}
                    />
                    <Input
                      label={t('pim.settings.media.defaultAltText')}
                      placeholder={t('pim.settings.media.defaultAltTextPlaceholder')}
                      {...register('media.defaultAltText')}
                    />
                    <Input
                      label={t('pim.settings.media.watermarkText')}
                      placeholder={t('pim.settings.media.watermarkTextPlaceholder')}
                      {...register('media.watermarkText')}
                    />
                    <Input
                      label={t('pim.settings.media.cdnUrl')}
                      placeholder={t('pim.settings.media.cdnUrlPlaceholder')}
                      {...register('media.cdnUrl')}
                    />
                  </div>

                  <div className="space-y-3">
                    <h4 className="text-sm font-medium text-gray-900">{t('pim.settings.media.processing')}</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <Checkbox
                        label={t('pim.settings.media.autoOptimizeImages')}
                        {...register('media.autoOptimizeImages')}
                      />
                      <Checkbox
                        label={t('pim.settings.media.generateThumbnails')}
                        {...register('media.generateThumbnails')}
                      />
                      <Checkbox
                        label={t('pim.settings.media.enableWatermark')}
                        {...register('media.enableWatermark')}
                      />
                      <Checkbox
                        label={t('pim.settings.media.enableCdn')}
                        {...register('media.enableCdn')}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('pim.settings.media.allowedFileTypes')}
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      {FILE_TYPE_OPTIONS.map((type) => (
                        <Checkbox
                          key={type.value}
                          label={type.label}
                          value={type.value}
                          {...register('media.allowedFileTypes')}
                        />
                      ))}
                    </div>
                  </div>

                  <Textarea
                    label={t('pim.settings.media.thumbnailSizes')}
                    placeholder={t('pim.settings.media.thumbnailSizesPlaceholder')}
                    {...register('media.thumbnailSizes')}
                  />
                </CardContent>
              </Card>
            )}

            {/* API & Security Settings */}
            {activeTab === 'api' && (
              <Card>
                <CardHeader>
                  <CardTitle>{t('pim.settings.api.title')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label={t('pim.settings.api.defaultRateLimit')}
                      type="number"
                      {...register('api.defaultRateLimit', { valueAsNumber: true })}
                    />
                    <Input
                      label={t('pim.settings.api.cacheTimeout')}
                      type="number"
                      {...register('api.cacheTimeout', { valueAsNumber: true })}
                    />
                    <Select
                      label={t('pim.settings.api.logLevel')}
                      {...register('api.logLevel')}
                      options={[
                        { value: 'error', label: t('pim.settings.api.logLevels.error') },
                        { value: 'warn', label: t('pim.settings.api.logLevels.warn') },
                        { value: 'info', label: t('pim.settings.api.logLevels.info') },
                        { value: 'debug', label: t('pim.settings.api.logLevels.debug') }
                      ]}
                    />
                  </div>

                  <div className="space-y-3">
                    <h4 className="text-sm font-medium text-gray-900">{t('pim.settings.api.features')}</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <Checkbox
                        label={t('pim.settings.api.enablePublicApi')}
                        {...register('api.enablePublicApi')}
                      />
                      <Checkbox
                        label={t('pim.settings.api.requireAuthentication')}
                        {...register('api.enableAuthentication')}
                      />
                      <Checkbox
                        label={t('pim.settings.api.enableCors')}
                        {...register('api.enableCors')}
                      />
                      <Checkbox
                        label={t('pim.settings.api.enableLogging')}
                        {...register('api.enableLogging')}
                      />
                      <Checkbox
                        label={t('pim.settings.api.enableCaching')}
                        {...register('api.enableCaching')}
                      />
                    </div>
                  </div>

                  <Textarea
                    label={t('pim.settings.api.corsOrigins')}
                    placeholder={t('pim.settings.api.corsOriginsPlaceholder')}
                    {...register('api.corsOrigins')}
                  />
                </CardContent>
              </Card>
            )}

            {/* Backup Settings */}
            {activeTab === 'backup' && (
              <Card>
                <CardHeader>
                  <CardTitle>{t('pim.settings.backup.title')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Select
                      label={t('pim.settings.backup.frequency')}
                      {...register('backup.backupFrequency')}
                      options={[
                        { value: 'daily', label: t('pim.settings.backup.frequencies.daily') },
                        { value: 'weekly', label: t('pim.settings.backup.frequencies.weekly') },
                        { value: 'monthly', label: t('pim.settings.backup.frequencies.monthly') }
                      ]}
                    />
                    <Input
                      label={t('pim.settings.backup.retention')}
                      type="number"
                      {...register('backup.backupRetention', { valueAsNumber: true })}
                    />
                    <Input
                      label={t('pim.settings.backup.location')}
                      placeholder={t('pim.settings.backup.locationPlaceholder')}
                      {...register('backup.backupLocation')}
                    />
                  </div>

                  <div className="space-y-3">
                    <h4 className="text-sm font-medium text-gray-900">{t('pim.settings.backup.options')}</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <Checkbox
                        label={t('pim.settings.backup.enableAutoBackup')}
                        {...register('backup.enableAutoBackup')}
                      />
                      <Checkbox
                        label={t('pim.settings.backup.includeMedia')}
                        {...register('backup.includeMedia')}
                      />
                      <Checkbox
                        label={t('pim.settings.backup.enableEncryption')}
                        {...register('backup.enableEncryption')}
                      />
                      <Checkbox
                        label={t('pim.settings.backup.notifyOnBackup')}
                        {...register('backup.notifyOnBackup')}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Save Button */}
            <div className="flex justify-end">
              <Button
                type="submit"
                disabled={updateMutation.isLoading || !hasUnsavedChanges}
                className="min-w-[120px]"
              >
                {updateMutation.isLoading ? t('pim.settings.saving') : t('pim.settings.saveSettings')}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};